import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

function StarRating({ rating }) {
 const num = parseFloat(rating || 0);
 const full = Math.floor(num);
 const half = num - full >= 0.5;
 const empty = 5 - full - (half ? 1 : 0);
 return (
 <span className="inline-flex items-center gap-0.5">
 {[...Array(full)].map((_, i) => <span key={`f${i}`} className="text-yellow-400 text-lg">★</span>)}
 {half && <span className="text-yellow-400 text-lg">★</span>}
 {[...Array(empty)].map((_, i) => <span key={`e${i}`} className="text-neutral-300 text-lg">★</span>)}
 </span>
 );
}

export default function PublicWorkerProfile() {
 const { userId } = useParams();
 const [worker, setWorker] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

 useEffect(() => {
 const fetchWorker = async () => {
 try {
 const { data } = await axios.get(`/api/worker/${userId}/public`);
 setWorker(data);
 } catch (err) {
 setError(err.response?.data?.error || 'Worker not found');
 } finally {
 setLoading(false);
 }
 };
 fetchWorker();
 }, [userId]);

 if (loading) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
 <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 if (error || !worker) {
 return (
 <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
 <div className="text-center">
 <p className="text-4xl mb-4"></p>
 <h2 className="text-xl font-semibold text-neutral-900 mb-2">Worker Not Found</h2>
 <p className="text-neutral-500 mb-6">{error || 'This profile is not available'}</p>
 <Link to="/" className="text-brand-600 font-medium hover:underline">Go home</Link>
 </div>
 </div>
 );
 }

 const initials = (worker.name || 'W').split(' ').map(s => s[0]).join('').toUpperCase().slice(0, 2);

 return (
 <div className="min-h-screen bg-neutral-50">
 <SEOHelmet title={worker.name || 'Worker Profile'} description={`View ${worker.name}'s profile on ShiftMatch. ${worker.bio || ''}`} />

 <div className="max-w-2xl mx-auto px-4 py-12">
 {/* Profile Card */}
 <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden mb-6">
 {/* Cover */}
 <div className="h-32 bg-gradient-to-r from-brand-400 to-brand-600" />

 {/* Avatar */}
 <div className="px-8 pb-8">
 <div className="flex justify-center -mt-16 mb-4">
 {worker.photo_url ? (
 <img src={worker.photo_url} alt={worker.name} className="w-28 h-28 rounded-full border-4 border-white object-cover shadow-md" />
 ) : (
 <div className="w-28 h-28 rounded-full border-4 border-white bg-brand-100 flex items-center justify-center text-2xl font-bold text-brand-700 shadow-md">
 {initials}
 </div>
 )}
 </div>

 {/* Name & Badge */}
 <div className="text-center mb-6">
 <div className="flex items-center justify-center gap-2 mb-1">
 <h1 className="text-2xl font-bold text-neutral-900">{worker.name}</h1>
 {worker.id_verified && (
 <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">✓ Verified</span>
 )}
 </div>
 {worker.location && (
 <p className="text-neutral-500 text-sm"> {worker.location}</p>
 )}
 </div>

 {/* Stats */}
 <div className="grid grid-cols-3 gap-4 text-center mb-6">
 <div className="bg-neutral-50 rounded-xl p-4">
 <p className="text-2xl font-bold text-neutral-900">{worker.avg_rating || '—'}</p>
 <p className="text-xs text-neutral-500 mt-1">
 <StarRating rating={worker.avg_rating} />
 </p>
 <p className="text-xs text-neutral-400">{worker.total_ratings || 0} ratings</p>
 </div>
 <div className="bg-neutral-50 rounded-xl p-4">
 <p className="text-2xl font-bold text-brand-600">{worker.completed_shifts || 0}</p>
 <p className="text-xs text-neutral-500 mt-1">Shifts Completed</p>
 </div>
 <div className="bg-neutral-50 rounded-xl p-4">
 <p className="text-2xl font-bold text-neutral-900">{worker.reliability_score ? `${worker.reliability_score}%` : '—'}</p>
 <p className="text-xs text-neutral-500 mt-1">Reliability</p>
 </div>
 </div>

 {/* Bio */}
 {worker.bio && (
 <div className="mb-6">
 <h3 className="text-sm font-semibold text-neutral-700 mb-2">About</h3>
 <p className="text-neutral-600 text-sm">{worker.bio}</p>
 </div>
 )}

 {/* Details */}
 <div className="space-y-3">
 {worker.experience_level && (
 <div className="flex justify-between text-sm">
 <span className="text-neutral-500">Experience</span>
 <span className="font-medium text-neutral-900 capitalize">{worker.experience_level.replace('_', ' ')}</span>
 </div>
 )}
 {worker.hourly_rate_range && (
 <div className="flex justify-between text-sm">
 <span className="text-neutral-500">Hourly Rate</span>
 <span className="font-medium text-brand-700">{worker.hourly_rate_range}</span>
 </div>
 )}
 {worker.availability && (
 <div className="flex justify-between text-sm">
 <span className="text-neutral-500">Availability</span>
 <span className="font-medium text-neutral-900 capitalize">{worker.availability}</span>
 </div>
 )}
 </div>

 {/* Certifications */}
 {worker.certs && worker.certs.length > 0 && (
 <div className="mt-6 pt-6 border-t border-neutral-100">
 <h3 className="text-sm font-semibold text-neutral-700 mb-3">Verified Certifications</h3>
 <div className="flex flex-wrap gap-2">
 {worker.certs.map((cert, i) => (
 <span key={i} className="bg-brand-50 text-brand-700 border border-brand-200 px-3 py-1.5 rounded-lg text-xs font-medium">
 ✓ {cert}
 </span>
 ))}
 </div>
 </div>
 )}
 </div>
 </div>

 {/* Availability Calendar (from localStorage for demo) */}
 {(() => {
   try {
     const avail = JSON.parse(localStorage.getItem('sm_availability') || '{}');
     const days = Object.keys(avail);
     if (days.length === 0) return null;
     return (
       <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden mb-6 p-6">
         <h3 className="text-sm font-semibold text-neutral-700 mb-3">📅 Availability</h3>
         <div className="grid grid-cols-7 gap-1">
           {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(day => {
             const isAvailable = !!avail[day];
             return (
               <div key={day} className={`text-center p-2 rounded-lg ${isAvailable ? 'bg-brand-50 border border-brand-200' : 'bg-neutral-50'}`}>
                 <p className={`text-[10px] font-bold ${isAvailable ? 'text-brand-700' : 'text-neutral-300'}`}>{day}</p>
                 {isAvailable && avail[day].map(t => (
                   <p key={t} className="text-[8px] text-brand-500 mt-0.5">{t.slice(0,3)}</p>
                 ))}
               </div>
             );
           })}
         </div>
       </div>
     );
   } catch { return null; }
 })()}

 {/* Back link */}
 <div className="text-center">
 <Link to="/" className="text-brand-600 font-medium hover:underline text-sm">← Back to ShiftMatch</Link>
 </div>
 </div>
 </div>
 );
}
